import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = '/api';

// ── Auth helpers (shared with App.js) ──
function getAuthToken() {
  return localStorage.getItem('etoro_token') || '';
}
function clearAuthToken() {
  localStorage.removeItem('etoro_token');
}

async function authedFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const resp = await fetch(url, { ...options, headers });
  if (resp.status === 401) {
    clearAuthToken();
    window.location.reload();
    return;
  }
  return resp;
}

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return 'N/A';
  return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return 'N/A';
  return (num >= 0 ? '+' : '') + num.toFixed(2) + '%';
};

const getBeatClass = (beat) => {
  if (beat === true) return 'positive';
  if (beat === false) return 'negative';
  return '';
};

const getBeatIcon = (beat) => {
  if (beat === true) return '✓';
  if (beat === false) return '✗';
  return '—';
};

function EarningsDashboard() {
  const [symbol, setSymbol] = useState('AAPL');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEarnings = useCallback(async (sym = symbol) => {
    if (!sym.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authedFetch(`${API_BASE}/earnings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: sym.toUpperCase() }),
      });
      const result = await response.json();
      if (!response.ok || result.error) throw new Error(result.error || `HTTP ${response.status}`);
      setData(result);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEarnings();
  };

  return (
    <div className="earnings-dashboard">
      <div className="section-header">
        <h2>Earnings Reports</h2>
        <form onSubmit={handleSearch} className="symbol-input-group">
          <input
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="Enter symbol (e.g., AAPL, TSLA)"
            className="symbol-input"
            maxLength={10}
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? 'Loading...' : 'Load'}
          </button>
        </form>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!data && !loading && !error && (
        <p className="text-muted">Enter a stock symbol to view earnings data</p>
      )}

      {data && (
        <div className="earnings-content">
          {/* Latest Quarter */}
          {data.latest_quarter && (
            <div className="stat-card-grid">
              <div className="stat-card">
                <div className="stat-label">Latest Quarter</div>
                <div className="stat-value">{data.latest_quarter.period}</div>
                <div className="stat-sub">Reported: {data.latest_quarter.reported_date}</div>
                <div className="stat-sub">{data.latest_quarter.report_time}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Reported EPS</div>
                <div className="stat-value">{formatNumber(data.latest_quarter.reported_eps)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Estimated EPS</div>
                <div className="stat-value">
                  {data.latest_quarter.estimated_eps !== null ? formatNumber(data.latest_quarter.estimated_eps) : 'N/A'}
                </div>
              </div>
              <div className={`stat-card ${getBeatClass(data.latest_quarter.beat_estimate)}`}>
                <div className="stat-label">Surprise</div>
                <div className="stat-value">
                  {data.latest_quarter.surprise_pct !== null ? formatPercent(data.latest_quarter.surprise_pct) : 'N/A'} {getBeatIcon(data.latest_quarter.beat_estimate)}
                </div>
              </div>
              <div className={`stat-card ${getBeatClass(data.latest_quarter.beat_estimate)}`}>
                <div className="stat-label">Beat Estimate</div>
                <div className="stat-value">
                  {data.latest_quarter.beat_estimate === true ? 'Yes' : data.latest_quarter.beat_estimate === false ? 'No' : 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Previous Quarter */}
          {data.previous_quarter && (
            <div className="earnings-section">
              <h3>Previous Quarter: {data.previous_quarter.period}</h3>
              <div className="stat-card-grid">
                <div className="stat-card">
                  <div className="stat-label">Reported EPS</div>
                  <div className="stat-value">{formatNumber(data.previous_quarter.reported_eps)}</div>
                </div>
                <div className={`stat-card ${getBeatClass(data.previous_quarter.beat_estimate)}`}>
                  <div className="stat-label">Surprise</div>
                  <div className="stat-value">
                    {data.previous_quarter.surprise_pct !== null ? formatPercent(data.previous_quarter.surprise_pct) : 'N/A'}
                  </div>
                </div>
                <div className={`stat-card ${getBeatClass(data.previous_quarter.beat_estimate)}`}>
                  <div className="stat-label">Beat Estimate</div>
                  <div className="stat-value">
                    {data.previous_quarter.beat_estimate === true ? 'Yes' : data.previous_quarter.beat_estimate === false ? 'No' : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Last 4 Quarters Table */}
          {data.last_4_quarters && data.last_4_quarters.length > 0 && (
            <div className="earnings-section">
              <h3>Last 4 Quarters</h3>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Reported EPS</th>
                      <th>Estimated EPS</th>
                      <th>Surprise</th>
                      <th>Beat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.last_4_quarters.map((q) => (
                      <tr key={q.period}>
                        <td>{q.period}</td>
                        <td>{formatNumber(q.reported_eps)}</td>
                        <td>{q.estimated_eps !== null ? formatNumber(q.estimated_eps) : 'N/A'}</td>
                        <td className={getBeatClass(q.beat_estimate)}>
                          {q.surprise_pct !== null ? formatPercent(q.surprise_pct) : 'N/A'}
                        </td>
                        <td>{getBeatIcon(q.beat_estimate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary Stats */}
          <div className="earnings-section">
            <h3>Summary</h3>
            <div className="stat-card-grid">
              <div className="stat-card">
                <div className="stat-label">Beat Rate (Last 4)</div>
                <div className="stat-value">{data.beat_rate_last_4.toFixed(1)}%</div>
              </div>
              <div className={`stat-card ${data.avg_surprise_pct >= 0 ? 'positive' : 'negative'}`}>
                <div className="stat-label">Avg Surprise</div>
                <div className="stat-value">{data.avg_surprise_pct.toFixed(2)}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Quarters Analyzed</div>
                <div className="stat-value">{data.last_4_quarters?.length || 0}</div>
              </div>
            </div>
          </div>

          {/* Annual Trend */}
          {data.annual_trend && data.annual_trend.length > 0 && (
            <div className="earnings-section">
              <h3>Annual EPS Trend (Last {data.annual_trend.length} Years)</h3>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Year</th><th>EPS</th></tr>
                  </thead>
                  <tbody>
                    {data.annual_trend.map((a) => (
                      <tr key={a.year}>
                        <td>{a.year}</td>
                        <td>{formatNumber(a.eps)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EarningsDashboard;